/**
 * Type Icons Helper
 *
 * UI utility that maps data types to Fluent UI icons for display in the token picker.
 * These icons are purely for visual representation - they do not affect token data.
 */

import React from 'react';
import { tokens } from '@coreai/fluentui-react';
import {
  TextTRegular,
  NumberSymbolRegular,
  ToggleLeftRegular,
  BracesVariableRegular,
  TextBulletListSquareRegular,
  DocumentRegular,
  CalendarRegular,
} from '@fluentui/react-icons';

export type DataType = 'string' | 'integer' | 'number' | 'boolean' | 'object' | 'array' | 'any' | string;

// Inline styles for icons (avoiding hooks to support conditional rendering)
const iconStyle: React.CSSProperties = {
  fontSize: tokens.fontSizeBase300,
  color: tokens.colorNeutralForeground2,
  flexShrink: 0,
};

const iconStyleSmall: React.CSSProperties = {
  fontSize: tokens.fontSizeBase200,
  color: tokens.colorNeutralForeground2,
  flexShrink: 0,
};

/**
 * Get the appropriate icon component for a data type
 * All icons are monochrome - differentiated by shape only
 *
 * Note: This function does not use hooks, so it can be called conditionally
 * or inside render callbacks without violating React rules of hooks.
 */
export function getTypeIcon(type: DataType | undefined, size: 'small' | 'normal' = 'normal'): React.ReactNode {
  const style = size === 'small' ? iconStyleSmall : iconStyle;
  // Handle cases where type might be an object (e.g., schema) instead of a string
  const typeStr = typeof type === 'string' ? type : 'any';
  const normalizedType = typeStr.toLowerCase();

  switch (normalizedType) {
    case 'string':
      return <TextTRegular style={style} aria-hidden="true" />;
    case 'integer':
    case 'number':
      return <NumberSymbolRegular style={style} aria-hidden="true" />;
    case 'boolean':
      return <ToggleLeftRegular style={style} aria-hidden="true" />;
    case 'object':
      return <BracesVariableRegular style={style} aria-hidden="true" />;
    case 'array':
      return <TextBulletListSquareRegular style={style} aria-hidden="true" />;
    case 'datetime':
    case 'date':
    case 'time':
      return <CalendarRegular style={style} aria-hidden="true" />;
    default:
      // For 'any' or unknown types
      return <DocumentRegular style={style} aria-hidden="true" />;
  }
}

/**
 * Check if a type can have children (is expandable)
 */
export function isExpandableType(type: string | undefined): boolean {
  // Handle cases where type might be an object instead of a string
  const typeStr = typeof type === 'string' ? type : '';
  const normalizedType = typeStr.toLowerCase();
  return normalizedType === 'object' || normalizedType === 'array';
}
